// ==========================================================================
// Home Page - Main app with bottom navigation
// Tabs: Prepare, Weather
// ==========================================================================

import {
  umbrellaIcon, umbrellaActiveIcon,
  navWeatherIcon, navWeatherActiveIcon,
} from '@/assets';
import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { usePWAInstall, PWAInstallModal, PWAInstallBanner } from '@/components/common/PWAInstallPrompt';
import { LoanOfferingModal, LoanOfferingBanner } from '@/components/common/LoanOffering';
import { useLoanOffering } from '@/hooks/useLoanOffering';
import { PreAlertCard, PreAlertModal } from '@/components/common/PreAlert';
import { AirQualityCard } from '@/components/weather/AirQualityCard';
import { LocationPicker } from '@/components/location/LocationPicker';
import { usePreAlertActions, useActivatePreAlertAction, preAlertKeys } from '@/hooks/usePreAlertActions';
import { usePreAlertSurface } from '@/hooks/usePreAlertSurface';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { useQueryClient } from '@tanstack/react-query';
import { useUser } from '@/hooks/useUser';
import { useWeather } from '@/hooks/useWeather';
import { useAlert, useInvalidateAlert } from '@/hooks/useAlert';
import {
  useActions,
  useActionDetail,
  useActivateAction,
  useCompleteStep,
  useUploadEvidence,
} from '@/hooks/useActions';
import i18n from '@/i18n';
import { getWeatherEmoji, getWeatherDescription } from '@/utils/weatherIcons';
import { MapPin } from 'lucide-react';

import type { WeatherCode } from '@/types/weather.types';
import type { Alert } from '@/types/alert.types';
import type {
  UserAction,
  UserActionDetail,
  UserActionStep,
  ActionStatus,
} from '@/types/action.types';

const ALERT_COLORS: Record<string, { bg: string; text: string; badge: string }> = {
  red:    { bg: '#dc2626', text: '#ffffff', badge: '#991b1b' },
  orange: { bg: '#ea580c', text: '#ffffff', badge: '#9a3412' },
  yellow: { bg: '#eab308', text: '#422006', badge: '#854d0e' },
};

const DEFAULT_ALERT_COLORS = ALERT_COLORS.yellow;

// ==========================================================================
// Pull to Refresh
// ==========================================================================
interface PullToRefreshProps {
  onRefresh: () => Promise<void>;
  children: React.ReactNode;
}

function PullToRefresh({ onRefresh, children }: PullToRefreshProps) {
  const { t } = useTranslation();
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startYRef = useRef(0);
  const pullingRef = useRef(false);
  const scrolledRef = useRef(false); // did native scroll happen during this touch?
  const containerRef = useRef<HTMLDivElement>(null);

  const THRESHOLD = 60;

  // Listen for native scroll events — if any fire during a touch, the user is scrolling content
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const onScroll = () => { scrolledRef.current = true; };
    container.addEventListener('scroll', onScroll, { passive: true });
    return () => container.removeEventListener('scroll', onScroll);
  }, []);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (refreshing) return;
    startYRef.current = e.touches[0].clientY;
    pullingRef.current = false;
    scrolledRef.current = false;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (refreshing) return;
    const container = containerRef.current;
    if (!container) return;

    // If native scroll happened during this touch gesture, never activate pull
    if (scrolledRef.current) return;

    // Must be at the very top right now
    if (container.scrollTop > 0) return;

    const diff = e.touches[0].clientY - startYRef.current;
    if (diff <= 0) return; // only downward pulls

    if (!pullingRef.current) {
      // Begin pull — reset start so the indicator starts from zero
      pullingRef.current = true;
      startYRef.current = e.touches[0].clientY;
      return;
    }

    const pull = e.touches[0].clientY - startYRef.current;
    setPullDistance(Math.min(pull * 0.4, 100));
  };

  const handleTouchEnd = async () => {
    if (!pullingRef.current) {
      setPullDistance(0);
      return;
    }
    pullingRef.current = false;

    if (pullDistance >= THRESHOLD) {
      setRefreshing(true);
      try {
        await onRefresh();
      } finally {
        setRefreshing(false);
      }
    }
    setPullDistance(0);
  };

  return (
    <div
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      className="flex-1 overflow-auto pb-20 relative"
    >
      {pullDistance > 0 && !refreshing && (
        <div
          className="flex items-center justify-center overflow-hidden"
          style={{ height: pullDistance }}
        >
          <span className="text-xs text-gray-400">
            {pullDistance >= THRESHOLD ? t('common.releaseToRefresh') : t('common.pullToRefresh')}
          </span>
        </div>
      )}
      {refreshing && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-50/60">
          <div className="w-10 h-10 border-4 border-gray-200 rounded-full animate-spin" style={{ borderTopColor: 'var(--brand-primary)' }} />
        </div>
      )}
      {children}
    </div>
  );
}

// ==========================================================================
// Main Page
// ==========================================================================
export default function HomePage() {
  const { t } = useTranslation();
  const { data: user } = useUser();
  const { data: alert } = useAlert();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showModal, showBanner, dismissModal } = usePWAInstall();
  const loanOffer = useLoanOffering(alert?.loan_offering, !!alert, alert?.id);
  usePushNotifications(user?.id);

  // Pre-alert card on the home screen — the drawer no longer auto-opens;
  // it surfaces only when the user taps "Explore how" on the CTA card.
  const { data: preAlertActions = [] } = usePreAlertActions();
  const activatePreAlertMutation = useActivatePreAlertAction();
  const preAlertSurface = usePreAlertSurface({
    hasAlert: !!alert,
    hasActions: preAlertActions.length > 0,
  });

  // When the alert state changes — particularly when an alert disappears —
  // force a pre-alert refetch. The BE returns [] while an alert is live, so
  // the cached pre-alert response is stale the moment the alert clears.
  // Without this, the banner can stay hidden until a full app reload.
  const hasAlert = !!alert;
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: preAlertKeys.list() });
  }, [hasAlert, queryClient]);

  const handleRefresh = useCallback(async () => {
    await queryClient.invalidateQueries();
  }, [queryClient]);

  // Tab index: prepare=0, weather=1
  const TAB_ORDER = { prepare: 0, weather: 1 } as const;
  const activeTab = location.pathname.startsWith('/home/prepare') ? 'prepare' : 'weather';
  const activeIndex = TAB_ORDER[activeTab];

  // Track slide direction
  const prevIndexRef = useRef(activeIndex);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | 'none'>('none');

  useEffect(() => {
    if (prevIndexRef.current !== activeIndex) {
      setSlideDirection(activeIndex > prevIndexRef.current ? 'right' : 'left');
      prevIndexRef.current = activeIndex;
    }
  }, [activeIndex]);

  return (
    <div className="h-[100dvh] flex flex-col bg-gray-50 overflow-hidden">
      {showBanner && <PWAInstallBanner />}
      {showModal && <PWAInstallModal onDismiss={dismissModal} />}
      <PullToRefresh onRefresh={handleRefresh}>
        <div
          key={activeTab}
          className={`${
            slideDirection === 'right' ? 'animate-slide-in-right'
              : slideDirection === 'left' ? 'animate-slide-in-left'
              : ''
          }`}
        >
          <Routes>
            <Route index element={<Navigate to="weather" replace />} />
            <Route path="weather" element={
              alert
                ? <AlertView user={user} alert={alert} onShowSteps={(actionId: number) => navigate(`/home/prepare/${actionId}`)} loanBanner={{ show: loanOffer.showBanner, offeringType: loanOffer.offeringType, telLink: loanOffer.telLink, value: loanOffer.value }} />
                : <WeatherTab user={user} preAlertBanner={preAlertSurface.showBanner ? { onClick: preAlertSurface.openDrawer } : undefined} />
            } />
            <Route path="prepare" element={<PrepareTab user={user} />} />
            <Route path="prepare/:actionId" element={<ActionDetailPage user={user} />} />
          </Routes>
        </div>
      </PullToRefresh>

      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 shadow-lg">
        <div className="flex items-center justify-around h-16 px-4">
          <NavItem
            icon={umbrellaIcon}
            activeIcon={umbrellaActiveIcon}
            label={t('nav.prepare')}
            isActive={activeTab === 'prepare'}
            activeColor="#8b5cf6"
            onClick={() => navigate('/home/prepare')}
          />
          <NavItem
            icon={navWeatherIcon}
            activeIcon={navWeatherActiveIcon}
            label={t('nav.weather')}
            isActive={activeTab === 'weather'}
            activeColor="#eab308"
            onClick={() => navigate('/home/weather')}
          />
        </div>
        <div className="h-safe-bottom bg-white" />
      </nav>

      {/* Loan offering modal — rendered above everything (nav, tabs, pull-to-refresh) */}
      {loanOffer.showModal && (
        <LoanOfferingModal
          title={loanOffer.title}
          body={loanOffer.body}
          offeringType={loanOffer.offeringType}
          telLink={loanOffer.telLink}
          value={loanOffer.value}
          onDismiss={loanOffer.dismissModal}
        />
      )}

      {/* Pre-alert modal — list of actions with onboarding-style detail slide */}
      <PreAlertModal
        isOpen={preAlertSurface.drawerOpen}
        onClose={preAlertSurface.closeDrawer}
        actions={preAlertActions}
        isActivating={activatePreAlertMutation.isPending}
        onActivate={async (actionId) => {
          try {
            const userAction = await activatePreAlertMutation.mutateAsync(actionId);
            preAlertSurface.closeDrawer();
            navigate(`/home/prepare/${userAction.id}`);
          } catch {
            // Toast shown by axios interceptor — keep modal open so user can retry
          }
        }}
      />
    </div>
  );
}

// ==========================================================================
// Nav Item
// ==========================================================================
interface NavItemProps {
  icon: string;
  activeIcon: string;
  label: string;
  isActive: boolean;
  activeColor: string;
  onClick: () => void;
}

function NavItem({ icon, activeIcon, label, isActive, activeColor, onClick }: NavItemProps) {
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center justify-center gap-1 px-4 py-2 transition-opacity"
    >
      <img src={isActive ? activeIcon : icon} alt={label} className="w-6 h-6 object-contain" />
      <span className="text-[11px] font-medium" style={{ color: isActive ? activeColor : '#9ca3af' }}>
        {label}
      </span>
    </button>
  );
}

// ==========================================================================
// Checkmark Icon (animated)
// ==========================================================================
function CheckIcon({ animated = false, className = '' }: { animated?: boolean; className?: string }) {
  return (
    <svg
      className={`${animated ? 'check-pop' : ''} ${className}`}
      width="22"
      height="22"
      viewBox="0 0 22 22"
      fill="none"
    >
      <circle cx="11" cy="11" r="11" fill="#22c55e" />
      <path d="M6 11.5L9.5 15L16 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function EmptyCircle() {
  return (
    <div className="w-[22px] h-[22px] rounded-full border-2 border-gray-300 flex-shrink-0" />
  );
}

// ==========================================================================
// Empty Actions State
// ==========================================================================
function EmptyActionsState() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center text-center px-4 min-h-[60vh]">
      <h3 className="text-[17px] font-bold text-gray-500 mb-3">
        {t('prepare.emptyInProgress')}
      </h3>
      <p className="text-[14px] text-gray-500 leading-relaxed mb-8 max-w-[300px]">
        {t('prepare.emptyInProgressDescription')}
      </p>
    </div>
  );
}

// ==========================================================================
// Prepare Tab
// ==========================================================================
interface PrepareTabProps {
  user: ReturnType<typeof useUser>['data'];
}

function PrepareTab({ user }: PrepareTabProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [listTab, setListTab] = useState<'in_progress' | 'completed'>('in_progress');

  // Fetch actions from API
  const { data: allActions = [], isLoading, error } = useActions();

  // Filter actions by status
  const inProgressActions = allActions.filter(
    (a) => a.status === 'assigned' || a.status === 'active'
  );
  const completedActions = allActions.filter((a) => a.status === 'completed');

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <p className="text-gray-400">{t('prepare.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <p className="text-gray-400">{t('prepare.error')}</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-2xl font-bold text-gray-900">{t('prepare.title')}</h1>
        {user?.fsp?.appIcon && (
          <img src={user.fsp.appIcon} alt={user.fsp.name} className="w-10 h-10 rounded-full object-cover" />
        )}
      </div>
      <p className="text-gray-500 text-sm mb-6">
        {t('prepare.subtitle')}
      </p>

      {/* Tabs */}
      <div className="flex justify-center gap-4 mb-5">
        <button
          type="button"
          onClick={() => setListTab('in_progress')}
          className={`text-sm font-medium pb-1 ${listTab === 'in_progress' ? 'text-gray-900 border-b-2 border-gray-900' : 'text-gray-400'}`}
        >
          {t('prepare.inProgress')}
        </button>
        <button
          type="button"
          onClick={() => setListTab('completed')}
          className={`text-sm font-medium pb-1 ${listTab === 'completed' ? 'text-gray-900 border-b-2 border-gray-900' : 'text-gray-400'}`}
        >
          {t('prepare.completed')}
        </button>
      </div>

      <div className="flex flex-col gap-4">
        {listTab === 'in_progress' && inProgressActions.map(action => (
          <ActionCard key={action.id} action={action} onClick={() => navigate(`/home/prepare/${action.id}`)} />
        ))}
        {listTab === 'completed' && completedActions.map(action => (
          <ActionCard key={action.id} action={action} onClick={() => navigate(`/home/prepare/${action.id}`)} />
        ))}
        {listTab === 'in_progress' && inProgressActions.length === 0 && (
          <EmptyActionsState />
        )}
        {listTab === 'completed' && completedActions.length === 0 && (
          <div className="flex flex-col items-center justify-center text-center px-4 min-h-[60vh]">
            <h3 className="text-[17px] font-bold text-gray-500 mb-3">
              {t('prepare.emptyCompleted')}
            </h3>
          </div>
        )}
      </div>
    </div>
  );
}

// ==========================================================================
// Action Detail Page (URL-routed with back arrow header)
// ==========================================================================
interface ActionDetailPageProps {
  user: ReturnType<typeof useUser>['data'];
}

function ActionDetailPage({ user }: ActionDetailPageProps) {
  const { t } = useTranslation();
  const { actionId } = useParams<{ actionId: string }>();
  const navigate = useNavigate();
  const id = actionId ? parseInt(actionId, 10) : null;

  const { data: activeAction, isLoading } = useActionDetail(id);

  const headerRow = (
    <div className="flex items-center mb-6">
      <button
        type="button"
        onClick={() => navigate('/home/prepare')}
        className="p-2 -ml-2"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
      </button>
      <h1 className="text-lg font-bold text-gray-900 flex-1 text-center">{t('prepare.backToPrepare')}</h1>
      {user?.fsp?.appIcon ? (
        <img src={user.fsp.appIcon} alt={user.fsp.name} className="w-10 h-10 rounded-full object-cover" />
      ) : (
        <div className="w-10" />
      )}
    </div>
  );

  if (isLoading) {
    return (
      <div className="p-6">
        {headerRow}
        <div className="flex items-center justify-center min-h-[40vh]">
          <p className="text-gray-400">{t('prepare.loadingAction')}</p>
        </div>
      </div>
    );
  }

  if (!activeAction) {
    return (
      <div className="p-6">
        {headerRow}
        <div className="flex items-center justify-center min-h-[40vh]">
          <p className="text-gray-400">{t('prepare.actionNotFound')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {headerRow}
      <ActionDetail action={activeAction} />
    </div>
  );
}

// ==========================================================================
// Action Card (list view)
// ==========================================================================
function ActionCard({ action, onClick }: { action: UserAction; onClick: () => void }) {
  const { t } = useTranslation();
  const statusConfig: Record<ActionStatus, { color: string; bg: string; text: string; icon: string }> = {
    assigned: { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)', text: t('prepare.statusAssigned'), icon: '📋' },
    active: { color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.1)', text: t('prepare.statusActive'), icon: '⚡' },
    expired: { color: '#6b7280', bg: 'rgba(107, 114, 128, 0.1)', text: t('prepare.statusExpired'), icon: '⏰' },
    completed: { color: '#22c55e', bg: 'rgba(34, 197, 94, 0.2)', text: t('prepare.statusCompleted'), icon: '⭐' },
  };

  const config = statusConfig[action.status];

  return (
    <button
      type="button"
      onClick={onClick}
      className="bg-white rounded-2xl p-5 shadow-sm text-left transition-all duration-200 active:scale-[0.98]"
      style={{ border: `2px solid ${config.color}` }}
    >
      <p className="text-[15px] font-bold text-gray-900 leading-snug mb-3">
        {action.action.name}
      </p>

      <div className="flex items-center gap-2 flex-wrap">
        {/* Status badge */}
        <div
          className="flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full"
          style={{ color: config.color, backgroundColor: config.bg }}
        >
          <span>{config.icon}</span>
          <span>{config.text}</span>
        </div>

        {/* Progress for active actions */}
        {action.status === 'active' && (
          <div className="flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
            <span>📊</span>
            <span>{action.progress.completed_steps}/{action.progress.total_steps} {t('prepare.steps')}</span>
          </div>
        )}
      </div>
    </button>
  );
}

// ==========================================================================
// Action Detail (step-by-step view)
// ==========================================================================
interface ActionDetailProps {
  action: UserActionDetail;
}

function ActionDetail({ action }: ActionDetailProps) {
  const { t } = useTranslation();
  const [expandedStep, setExpandedStep] = useState<number | null>(null);

  const activateMutation = useActivateAction();
  const completeStepMutation = useCompleteStep();
  const uploadMutation = useUploadEvidence();

  const allCompleted = action.steps.every(
    (s) => s.status === 'completed' || s.status === 'approved'
  );

  const isAssigned = action.status === 'assigned';
  const isActive = action.status === 'active';
  const isCompleted = action.status === 'completed';

  const progressPercent = action.progress.total_steps > 0
    ? Math.round((action.progress.completed_steps / action.progress.total_steps) * 100)
    : 0;

  const toggleStep = (stepId: number) => {
    setExpandedStep((prev) => (prev === stepId ? null : stepId));
  };

  const handleActivate = async () => {
    try {
      await activateMutation.mutateAsync(action.id);
    } catch {
      // Toast shown by axios interceptor
    }
  };

  const handleCompleteStep = async (stepId: number) => {
    try {
      await completeStepMutation.mutateAsync({ actionId: action.id, stepId });
      setExpandedStep(null);
    } catch {
      // Toast shown by axios interceptor
    }
  };

  const handleUploadEvidence = async (stepId: number, file: File) => {
    try {
      await uploadMutation.mutateAsync({ actionId: action.id, stepId, file });
    } catch {
      // Toast shown by axios interceptor
    }
  };

  // Status badge config
  const getStatusBadge = () => {
    if (isAssigned) return { text: t('prepare.statusAssigned'), icon: '📋' };
    if (isActive) return { text: `${action.progress.completed_steps}/${action.progress.total_steps} ${t('prepare.steps')}`, icon: '📊' };
    if (isCompleted) return { text: t('prepare.statusCompleted'), icon: '⭐' };
    return { text: t('prepare.statusAssigned'), icon: '📋' };
  };

  const statusBadge = getStatusBadge();

  return (
    <div className="fade-in">
      {/* Action summary card */}
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-200 mb-6">
        <p className="text-[15px] font-bold text-gray-900 leading-snug">
          {action.action.name}
        </p>

        <div className="flex items-center gap-2 flex-wrap mt-4">
          <span className="text-xs text-gray-500 flex items-center gap-1 bg-gray-100 px-2 py-1 rounded-full">
            <span>{statusBadge.icon}</span> {statusBadge.text}
          </span>
        </div>

        {/* Activate button for assigned actions */}
        {isAssigned && (
          <button
            type="button"
            onClick={handleActivate}
            disabled={activateMutation.isPending}
            className="w-full mt-3 py-3 rounded-xl text-white text-sm font-semibold bg-brand-primary disabled:opacity-50"
          >
            {activateMutation.isPending ? t('prepare.starting') : t('prepare.startAction')}
          </button>
        )}

        {/* Step-by-step heading */}
        <h3 className="text-base font-bold text-gray-900 mb-2 mt-6">{t('prepare.stepByStep')}</h3>
        <p className="text-sm text-gray-400 mb-5 leading-relaxed">
          {action.action.description || t('prepare.stepByStepDescription')}
        </p>

        {/* Progress bar for active actions */}
        {isActive && (
          <div className="mb-5">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-medium text-gray-500">{t('prepare.progress')}</span>
              <span className="text-xs font-semibold text-gray-700">
                {action.progress.completed_steps}/{action.progress.total_steps} {t('prepare.steps')} ({progressPercent}%)
              </span>
            </div>
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full bg-brand-primary transition-all duration-500"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
        )}

        {/* Steps */}
        <div className="flex flex-col gap-3 mb-6">
          {[...action.steps].sort((a, b) => a.order - b.order).map((step) => (
            <StepCard
              key={step.id}
              step={step}
              isExpanded={expandedStep === step.id}
              onToggle={() => toggleStep(step.id)}
              onComplete={() => handleCompleteStep(step.id)}
              onUpload={(file) => handleUploadEvidence(step.id, file)}
              isCompleting={completeStepMutation.isPending}
              isUploading={uploadMutation.isPending}
              actionStatus={action.status}
            />
          ))}
        </div>
      </div>

      {/* Action completed message */}
      {isCompleted && (
        <div className="bg-green-50 rounded-xl p-4 text-center">
          <span className="text-2xl mb-2 block">🎉</span>
          <p className="text-green-700 font-semibold">{t('prepare.actionCompleted')}</p>
        </div>
      )}

      {/* All steps done message for active actions */}
      {allCompleted && isActive && (
        <div className="bg-green-50 rounded-xl p-4 text-center">
          <span className="text-2xl mb-2 block">✅</span>
          <p className="text-green-700 font-semibold">{t('prepare.allStepsCompleted')}</p>
        </div>
      )}

    </div>
  );
}

// ==========================================================================
// Step Card (accordion)
// ==========================================================================
interface StepCardProps {
  step: UserActionStep;
  isExpanded: boolean;
  onToggle: () => void;
  onComplete: () => void;
  onUpload: (file: File) => void;
  isCompleting: boolean;
  isUploading: boolean;
  actionStatus: ActionStatus;
}

function StepCard({
  step,
  isExpanded,
  onToggle,
  onComplete,
  onUpload,
  isCompleting,
  isUploading,
  actionStatus,
}: StepCardProps) {
  const { t } = useTranslation();
  // Two separate inputs — one declared with `capture="environment"` (camera)
  // and one without (gallery). Toggling `capture` on a single input via JS
  // before `.click()` is unreliable on modern Android Chrome — it reads the
  // attribute at element-creation time, so the camera-vs-gallery decision
  // must be baked into the markup. See bug report 2026-05-18 ("Take Photo"
  // opening the gallery instead of the camera on Android).
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const autoCompletedRef = useRef(false);
  const [viewingImage, setViewingImage] = useState(false);

  const isDone = step.status === 'completed';
  const isApproved = step.status === 'approved';

  // Auto-complete evidence steps once approved
  useEffect(() => {
    if (isApproved && step.requires_evidence && actionStatus === 'active' && !autoCompletedRef.current) {
      autoCompletedRef.current = true;
      onComplete();
    }
  }, [isApproved, step.requires_evidence, actionStatus, onComplete]);
  const isValidating = step.status === 'evidence_uploaded';
  const isRejected = step.status === 'rejected';
  const canInteract = actionStatus === 'active' && !isDone && !isValidating;

  const borderClass = isDone || isApproved
    ? 'border-green-200'
    : isRejected
      ? 'border-red-200'
      : isValidating
        ? 'border-purple-200'
        : 'border-gray-200';

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        alert('File too large. Maximum size is 10MB.');
        return;
      }
      onUpload(file);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const handleTakePhoto = () => {
    cameraInputRef.current?.click();
  };

  const handleUploadFile = () => {
    galleryInputRef.current?.click();
  };

  const handleMarkCompleted = () => {
    onComplete();
  };

  return (
    <div className={`step-card bg-white rounded-xl border shadow-sm overflow-hidden ${borderClass}`}>
      {/* Hidden file inputs — separate elements for camera vs gallery. */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        className="hidden"
      />
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Header row */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-start gap-3 px-4 py-3.5 text-left"
      >
        <div className="mt-0.5 flex-shrink-0">
          {isDone || isApproved ? (
            <CheckIcon animated />
          ) : (
            <EmptyCircle />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold ${
            isDone || isApproved ? 'text-green-600' : isRejected ? 'text-red-600' : 'text-gray-900'
          }`}>
            {step.title}
          </p>
          <div className="mt-1 flex">
            <StepBadge step={step} />
          </div>
          {!isExpanded && !isDone && !step.requires_evidence && (
            <p className="text-xs text-gray-400 truncate mt-0.5">{step.description.split('\n')[0]}</p>
          )}
        </div>
      </button>

      {/* Expandable content */}
      <div className={`accordion-content ${isExpanded ? 'open' : ''}`}>
        <div className="accordion-inner">
          <div className="px-4 pb-4 pl-[52px]">
            <div className="text-sm text-gray-500 leading-relaxed mb-4 whitespace-pre-line">
              {step.description}
            </div>

            {/* Evidence preview if uploaded */}
            {step.evidence_url && (
              <div className="mb-4">
                <button type="button" onClick={() => setViewingImage(true)} className="block">
                  <img
                    src={step.evidence_url}
                    alt="Uploaded evidence"
                    className="rounded-lg max-h-40 object-cover"
                  />
                </button>
              </div>
            )}

            {/* Full-screen image viewer */}
            {viewingImage && step.evidence_url && (
              <div
                className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
                onClick={() => setViewingImage(false)}
              >
                <button
                  type="button"
                  onClick={() => setViewingImage(false)}
                  className="absolute top-4 right-4 text-white text-3xl font-light p-2"
                >
                  ✕
                </button>
                <img
                  src={step.evidence_url}
                  alt="Evidence"
                  className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg"
                />
              </div>
            )}

            {/* Rejected message */}
            {isRejected && (
              <p className="text-sm text-red-600 mb-3">
                {t('prepare.evidenceRejected')}
              </p>
            )}

            {/* Evidence step buttons */}
            {canInteract && step.requires_evidence && (step.status === 'pending' || isRejected) && (
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleTakePhoto}
                  disabled={isUploading}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 text-sm font-semibold disabled:opacity-50 ${
                    isRejected ? 'border-red-500 text-red-500' : 'border-brand-primary text-brand-primary'
                  }`}
                >
                  {isUploading ? '...' : '📷'} {t('prepare.takePhoto')}
                </button>
                <button
                  type="button"
                  onClick={handleUploadFile}
                  disabled={isUploading}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-white text-sm font-semibold disabled:opacity-50 ${
                    isRejected ? 'bg-red-500' : 'bg-brand-primary'
                  }`}
                >
                  {isUploading ? '...' : '🖼'} {t('prepare.uploadImage')}
                </button>
              </div>
            )}

            {/* Non-evidence step button */}
            {canInteract && !step.requires_evidence && step.status === 'pending' && (
              <button
                type="button"
                onClick={handleMarkCompleted}
                disabled={isCompleting}
                className="w-full py-2.5 rounded-xl border-2 border-green-400 text-green-600 text-sm font-semibold flex items-center justify-center gap-2 transition-colors active:bg-green-50 disabled:opacity-50"
              >
                {isCompleting ? t('prepare.completing') : `✓ ${t('prepare.markAsCompleted')}`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ==========================================================================
// Step Badge
// ==========================================================================
function StepBadge({ step }: { step: UserActionStep }) {
  const { t } = useTranslation();
  if (step.status === 'completed') {
    return (
      <span className="check-pop text-xs font-medium text-green-600 flex items-center gap-1 flex-shrink-0 bg-green-100 px-2 py-1 rounded-full">
        ✓ {t('prepare.stepCompleted')}
      </span>
    );
  }

  if (step.status === 'approved') {
    return (
      <span className="check-pop text-xs font-medium text-green-600 flex items-center gap-1 flex-shrink-0 bg-green-100 px-2 py-1 rounded-full">
        ✓ {t('prepare.stepApproved')}
      </span>
    );
  }

  if (step.status === 'evidence_uploaded') {
    return (
      <span className="soft-pulse text-xs font-medium text-purple-600 flex items-center gap-1 flex-shrink-0 bg-purple-50 px-2 py-1 rounded-full">
        ◉ {t('prepare.stepWaitingApproval')}
      </span>
    );
  }

  if (step.status === 'rejected') {
    return (
      <span className="text-xs font-medium text-red-600 flex items-center gap-1 flex-shrink-0 bg-red-50 px-2 py-1 rounded-full">
        ✕ {t('prepare.stepRejected')}
      </span>
    );
  }

  if (step.requires_evidence && step.status === 'pending') {
    return (
      <span className="text-xs font-medium text-gray-500 flex items-center gap-1 flex-shrink-0 bg-gray-100 px-2 py-1 rounded-full">
        🖼 {t('prepare.stepUploadRequired')}
      </span>
    );
  }

  return null;
}

// ==========================================================================
// Weather/Date helpers
// ==========================================================================
function formatForecastDay(dateStr: string, weekdayName?: string): { dayNum: string; dayName: string } {
  const date = new Date(dateStr + 'T00:00:00');
  const dayNum = String(date.getDate());

  if (weekdayName) {
    return { dayNum, dayName: weekdayName };
  }

  const localeMap: Record<string, string> = { am: 'am-ET', es: 'es-ES', en: 'en-US' };
  const locale = localeMap[i18n.language] ?? 'en-US';
  const dayName = date.toLocaleDateString(locale, { weekday: 'long' });
  return { dayNum, dayName };
}

/** Formats ISO date to "Wednesday, 17th" / "miércoles, 17" */
function formatAlertDate(isoDate: string): string {
  const date = new Date(isoDate);
  const locale = i18n.language === 'es' ? 'es-ES' : 'en-US';
  const dayName = date.toLocaleDateString(locale, { weekday: 'long' });
  const day = date.getDate();
  const suffix = getDaySuffix(day);
  return `${dayName}, ${day}${suffix}`;
}

function getDaySuffix(day: number): string {
  if (day >= 11 && day <= 13) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}

// ==========================================================================
// Weather Tab
// ==========================================================================
interface WeatherTabProps {
  user: ReturnType<typeof useUser>['data'];
  preAlertBanner?: { onClick: () => void };
}

function WeatherTab({ user, preAlertBanner }: WeatherTabProps) {
  const { t } = useTranslation();
  const { data: weather, isLoading, error } = useWeather();
  const location = user?.location;
  const fsp = user?.fsp;
  const [isLocationPickerOpen, setIsLocationPickerOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <p className="text-gray-400">{t('weather.loading')}</p>
      </div>
    );
  }

  if (error || !weather) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <p className="text-gray-400">{t('weather.error')}</p>
      </div>
    );
  }

  const currentDescription = weather.current.weather_label ?? getWeatherDescription(weather.current.weather_code);
  const currentEmoji = getWeatherEmoji(weather.current.weather_code);
  const county = location?.municipality || weather.location?.name || 'Your area';

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-xl font-bold text-gray-900">{t('weather.title')}</h1>
        {fsp?.appIcon && (
          <img src={fsp.appIcon} alt={fsp.name} className="w-9 h-9 rounded-full object-cover" />
        )}
      </div>

      <div className="rounded-2xl p-5 text-white mb-2" style={{ backgroundColor: '#4a7c7e' }}>
        <div className="flex items-start justify-between mb-3">
          <div className="bg-white/20 rounded-full px-3 py-1">
            <span className="text-sm font-medium">{county}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-3xl">{currentEmoji}</span>
            <span className="text-4xl font-bold">{Math.round(weather.current.temperature)}°</span>
          </div>
        </div>
        <p className="text-white/90 text-sm leading-relaxed">
          {currentDescription}. {t('weather.feelsLike', { temp: Math.round(weather.current.feels_like), humidity: weather.current.humidity })}
        </p>
        <button
          type="button"
          onClick={() => setIsLocationPickerOpen(true)}
          className="mt-3 w-full flex items-center justify-center gap-1.5 text-[12px] font-medium text-white/90 bg-white/10 rounded-full py-1.5 hover:bg-white/20"
        >
          <MapPin className="w-[13px] h-[13px]" />
          {t('weather.changeLocation')}
        </button>
      </div>
      <p className="text-[11px] text-gray-400 mb-4 px-1">{t('weather.locationCaption')}</p>

      {weather.air_quality && <AirQualityCard airQuality={weather.air_quality} />}

      <div className="flex flex-col gap-3">
        {weather.forecast.slice(0, 3).map((day) => {
          const { dayNum, dayName } = formatForecastDay(day.date, day.weekday_name);
          const emoji = getWeatherEmoji(day.weather_code as WeatherCode);
          const desc = day.weather_label ?? getWeatherDescription(day.weather_code as WeatherCode);

          return (
            <div key={day.date} className="flex items-center bg-white rounded-xl px-4 py-3 shadow-sm border border-gray-200">
              <span className="text-2xl mr-3">{emoji}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900">{dayNum} {dayName}</p>
                <p className="text-xs text-gray-400 truncate">{desc}</p>
              </div>
              <span className="text-sm font-semibold text-gray-900 ml-2">{Math.round(day.high)}°C</span>
            </div>
          );
        })}
      </div>

      {preAlertBanner && (
        <div className="mt-6">
          <PreAlertCard onClick={preAlertBanner.onClick} />
        </div>
      )}

      <LocationPicker
        isOpen={isLocationPickerOpen}
        onClose={() => setIsLocationPickerOpen(false)}
        variant="update"
      />
    </div>
  );
}

// ==========================================================================
// Countdown hook for alert event_date
// ==========================================================================
function useCountdown(eventDate: string | undefined, onExpire: () => void) {
  const [remaining, setRemaining] = useState('');
  const expiredRef = useRef(false);

  useEffect(() => {
    if (!eventDate) return;
    expiredRef.current = false;

    const tick = () => {
      const diff = new Date(eventDate).getTime() - Date.now();
      if (diff <= 0) {
        setRemaining('00:00:00');
        if (!expiredRef.current) {
          expiredRef.current = true;
          onExpire();
        }
        return;
      }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setRemaining(
        `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
      );
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [eventDate, onExpire]);

  return remaining;
}

// ==========================================================================
// Alert View
// ==========================================================================
interface AlertViewProps {
  user: ReturnType<typeof useUser>['data'];
  alert: Alert | null | undefined;
  onShowSteps: (actionId: number) => void;
  loanBanner?: { show: boolean; offeringType?: 'ussd' | 'url'; telLink?: string; value: string };
}

function AlertView({ user, alert, onShowSteps, loanBanner }: AlertViewProps) {
  const { t } = useTranslation();
  const { data: weather } = useWeather();
  // Fetch all user actions to show summary card
  const { data: actions = [] } = useActions();
  const featuredActions = actions.filter((a) => a.status === 'assigned' || a.status === 'active').slice(0, 3);
  const invalidateAlert = useInvalidateAlert();
  const activateMutation = useActivateAction();
  const [activatingId, setActivatingId] = useState<number | null>(null);

  const location = user?.location;
  const fsp = user?.fsp;
  const county = location?.municipality || weather?.location?.name || 'Your area';
  const userName = user?.name?.split(' ')[0] || 'there';
  const severity = alert?.severity || 'yellow';

  // Use alert severity from API, fallback to yellow for unknown levels
  const colors = ALERT_COLORS[severity] || DEFAULT_ALERT_COLORS;

  const currentEmoji = weather ? getWeatherEmoji(weather.current.weather_code) : '🌤️';
  const currentTemp = weather ? Math.round(weather.current.temperature) : 17;

  // Format event_date for orange/yellow alerts (e.g., "Wednesday, 17th")
  const eventDateFormatted = alert?.event_date ? formatAlertDate(alert.event_date) : '';

  // Live countdown to event_date — refetch alerts when it reaches 0
  const handleCountdownExpire = useCallback(() => {
    invalidateAlert();
  }, [invalidateAlert]);

  const countdown = useCountdown(alert?.event_date, handleCountdownExpire);

  // Get severity-specific i18n keys
  const severityKey = severity as 'red' | 'orange' | 'yellow';
  const alertMessage = t(`alert.${severityKey}.message`, { name: userName, date: eventDateFormatted });
  const alertContext = t(`alert.${severityKey}.context`, { defaultValue: '' });
  const alertTipLabel = t(`alert.${severityKey}.tipLabel`);
  const alertTipIcon = t(`alert.${severityKey}.tipIcon`);
  const alertTip = t(`alert.${severityKey}.tip`);
  const alertFooter = t(`alert.${severityKey}.footer`, { defaultValue: '' });

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-xl font-bold text-gray-900">{t('weather.title')}</h1>
        {fsp?.appIcon && (
          <img src={fsp.appIcon} alt={fsp.name} className="w-9 h-9 rounded-full object-cover" />
        )}
      </div>

      {/* Loan offering banner (subsequent visits) */}
      {loanBanner?.show && (
        <LoanOfferingBanner offeringType={loanBanner.offeringType} telLink={loanBanner.telLink} value={loanBanner.value} />
      )}

      <div className="rounded-2xl p-4 text-white mb-4 flex items-center justify-between" style={{ backgroundColor: '#4a7c7e' }}>
        <div className="bg-white/20 rounded-full px-3 py-1">
          <span className="text-sm font-medium">{county}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-2xl">{currentEmoji}</span>
          <span className="text-2xl font-bold">{currentTemp}°</span>
        </div>
      </div>

      <div className="rounded-2xl overflow-hidden mb-6" style={{ backgroundColor: colors.bg }}>
        <div className="p-5">
          <div className="flex items-start justify-between gap-2 mb-3">
            <h2 className="text-lg font-bold" style={{ color: colors.text }}>
              {t('alert.title', { town: county.toUpperCase() })}
            </h2>
            {countdown && (
              <div className="flex items-center gap-2 bg-black/10 rounded-full px-3 py-1.5 shrink-0 w-[120px] justify-center">
                <span className="text-sm">⏳</span>
                <span className="text-sm font-mono font-medium tabular-nums" style={{ color: colors.text }}>{countdown}</span>
              </div>
            )}
          </div>

          <p className="text-sm leading-relaxed mb-4" style={{ color: colors.text, opacity: 0.9 }}>
            {alertMessage}
          </p>

          {alertContext && (
            <p className="text-sm leading-relaxed mb-4" style={{ color: colors.text, opacity: 0.85 }}>
              {alertContext}
            </p>
          )}

          <div className="bg-white/20 rounded-xl p-4 mb-4">
            <div className="flex items-start gap-2 mb-2">
              <span className="text-base">{alertTipIcon}</span>
              <span className="text-sm font-semibold" style={{ color: colors.text }}>
                {alertTipLabel}
              </span>
            </div>
            <p className="text-sm leading-relaxed pl-6" style={{ color: colors.text, opacity: 0.9 }}>
              {alertTip}
            </p>
          </div>

          {alertFooter && (
            <p className="text-sm italic" style={{ color: colors.text, opacity: 0.8 }}>
              {alertFooter}
            </p>
          )}
        </div>
      </div>

      {featuredActions.length > 0 && (
        <>
          <p className="text-gray-400 text-sm mb-3">{t('prepare.recommendedActions')}</p>

          <div className="flex flex-col gap-3">
            {featuredActions.map((action) => (
              <div key={action.id} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-200">
                <p className="text-[15px] font-bold text-gray-900 leading-snug mb-4">
                  {action.action.name}
                </p>
                <div className="flex items-center justify-between mb-4">
                  <span className="inline-flex items-center gap-1.5 text-xs text-gray-500 bg-[#f5f0e8] px-2.5 py-1 rounded-full">
                    🕐 {t('prepare.timeEstimate')}
                  </span>
                  {action.action.incentive_appropriate && (
                    <span className="inline-flex items-center gap-1.5 text-xs text-gray-500 bg-[#f5f0e8] px-2.5 py-1 rounded-full">
                      💰 {t('prepare.cashSupport')}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  disabled={activateMutation.isPending}
                  onClick={async () => {
                    if (action.status === 'assigned') {
                      setActivatingId(action.id);
                      try {
                        await activateMutation.mutateAsync(action.id);
                      } catch {
                        setActivatingId(null);
                        return;
                      }
                    }
                    setActivatingId(null);
                    onShowSteps(action.id);
                  }}
                  className="w-full py-3 rounded-xl text-white text-sm font-semibold bg-brand-primary disabled:opacity-50"
                >
                  {activatingId === action.id
                    ? t('prepare.starting')
                    : action.status === 'active'
                      ? t('prepare.continueAction')
                      : t('prepare.startActionButton')}
                </button>
              </div>
            ))}
          </div>
        </>
      )}

    </div>
  );
}

